'use client';

import { useEffect } from 'react';

import { logger } from '@/lib/logger';
import { useLoggerConsentSync } from '@/lib/logger/hooks/useLoggerConsentSync';
import { useLoggerUserSync } from '@/lib/logger/hooks/useLoggerUserSync';
import '@/sentry.client.config';
import {
  initLoggingEssential,
  initLoggingEnhanced,
  setLoggerTenant,
} from '@/lib/logger/setup.client';

/**
 * Phase 1 — mount outside ConsentProvider.
 * Starts console interception and basic error capture immediately.
 */
export const LoggingEssential = () => {
  useEffect(() => {
    initLoggingEssential();

    window.onerror = function (
      message: string | Event,
      source?: string,
      lineno?: number,
      colno?: number,
      error?: Error,
    ) {
      const finalError =
        error instanceof Error
          ? error
          : typeof message === 'string'
            ? new Error(message)
            : new Error('Unknown error');

      logger.error(finalError, {
        source: source?.toString(),
        line: lineno,
        column: colno,
      });
    };

    window.onunhandledrejection = function (event) {
      const reason = event.reason;
      const error =
        reason instanceof Error ? reason : new Error(String(reason));
      logger.error(error, { source: 'unhandledrejection' });
    };
  }, []);

  return null;
};

/**
 * Phase 2 — mount inside ConsentProvider.
 * Syncs consent state and registers enhanced plugins (PostHog, Sentry)
 * and user/tenant context enhancers.
 */
export const LoggingBootstrap = ({ tenantId }: { tenantId?: string }) => {
  useLoggerConsentSync();

  useEffect(() => {
    initLoggingEnhanced();
  }, []);

  useEffect(() => {
    if (tenantId) setLoggerTenant(tenantId);
  }, [tenantId]);

  return null;
};

/**
 * Mount inside AuthProvider to sync the authenticated user into the logger pipeline.
 * Follows the same pattern as LoggingBootstrap / useLoggerConsentSync.
 */
export const LoggingUserSync = () => {
  useLoggerUserSync();
  return null;
};
